import dataHubManager, { DataHubStorageOperations } from '../../managers/data/DataHubManager'
import { ArmadoresHub, armadoresHubName } from './ArmadoresHub'
import { ChangesHub, changesHubName } from './ChangesHub'
import { EmbarcacionesHub, embarcacionesHubName } from './EmbarcacionesHub'
import { RepresentantesHub, representantesHubName } from './RepresentantesHub'
import { TripulantesHub, tripulantesHubName } from './TripulantesHub'
import { ViajesHub, viajesHubName } from './ViajesHub'

export interface StorageService {
  bucketData: any[]
  storageOperations: DataHubStorageOperations
  pipelinesOperations: Map<string, DataHubStorageOperations>
  startup: () => Promise<void>
}

export interface SubscriptionBridge<T> {
  subscribe: (storageID: string, subscriberID: string, callback: (data: T[]) => void) => void
  unsubscribe: (storageID: string, subscriberID: string) => void
}

export interface AirSubscription {
  subscriberID: string
  pipeline: string | null
  callback: (data: any[]) => void
}

export const defaultRefreshTime = 5 * 60 * 1000 // 5 minutos

// Encargado de iniciar los distintos hubs de datos, cada hub se encargará de manejar un tipo de dato específico (usuarios, armadores, tripulantes, etc) y sus respectivas transformaciones y pipelines.
// Cada hub tendrá su propia lógica de negocio para manejar los datos, pero todos estarán centralizados en este servicio para facilitar su gestión y mantenimiento.
class DataHubServices {
  private avaiableHubs: Map<string, StorageService> = new Map() // Aquí se almacenarán las instancias de los hubs disponibles
  private airSubscriptions: Map<string, Array<AirSubscription>> = new Map() // Para manejar las suscripciones a los hub services sin dependencias directas
  // Se crean subscripciones locales para cada hub service, si el hub ya está registrado se suscribe directamente, si no, se almacena la subscripción en airSubscriptions y se suscribe cuando el hub se registre

  constructor() {}

  public async startup() {
    this.addDefaultHubs() // Agregar los hubs por defecto (armadores, usuarios, tripulantes, etc)
    // Iniciar cada hub registrado
    const startPromises = Array.from(this.avaiableHubs.entries()).map(async ([hubName, _hubInstance]) => {
      await this.startHub(hubName)
    })
    await Promise.all(startPromises)
  }

  private addDefaultHubs() {
    const changesHub = new ChangesHub()
    this.registerHub(changesHubName, changesHub)
    if (!this.avaiableHubs.has(changesHubName)) this.airSubscriptions.set(changesHubName, [])

    const armadoresHub = new ArmadoresHub()
    this.registerHub(armadoresHubName, armadoresHub)
    if (!this.avaiableHubs.has(armadoresHubName)) this.airSubscriptions.set(armadoresHubName, [])

    const embarcacionesHub = new EmbarcacionesHub()
    this.registerHub(embarcacionesHubName, embarcacionesHub)
    if (!this.avaiableHubs.has(embarcacionesHubName)) this.airSubscriptions.set(embarcacionesHubName, [])

    const tripulantesHub = new TripulantesHub()
    this.registerHub(tripulantesHubName, tripulantesHub)
    if (!this.avaiableHubs.has(tripulantesHubName)) this.airSubscriptions.set(tripulantesHubName, [])

    const viajesHub = new ViajesHub()
    this.registerHub(viajesHubName, viajesHub)
    if (!this.avaiableHubs.has(viajesHubName)) this.airSubscriptions.set(viajesHubName, [])

    const representantesHub = new RepresentantesHub()
    this.registerHub(representantesHubName, representantesHub)
    if (!this.avaiableHubs.has(representantesHubName)) this.airSubscriptions.set(representantesHubName, [])
  }

  private async startHub(hubName: string) {
    const hub = this.getHub(hubName)
    console.log(`Starting hub: ${hubName}`, hub)
    if (hub) {
      await hub.startup()
      console.log(`Hub ${hubName} started successfully.`)

      // Suscribir a las subscripciones almacenadas en airSubscriptions para este hub
      if (this.airSubscriptions.has(hubName)) {
        const subscriptions = this.airSubscriptions.get(hubName)!
        subscriptions.forEach((subscription) => {
          if (subscription.pipeline) {
            dataHubManager.subscribePipeline(
              hubName,
              subscription.pipeline,
              subscription.subscriberID,
              subscription.callback,
            )
          } else {
            dataHubManager.subscribeStorage(hubName, subscription.subscriberID, subscription.callback)
          }
        })
        // Una vez suscritos, eliminar las subscripciones almacenadas para este hub
        this.airSubscriptions.delete(hubName)
      }
    } else {
      console.warn(`Hub with name ${hubName} is not registered. Cannot start.`)
    }
  }

  public registerHub<T extends StorageService>(hubName: string, hubInstance: T) {
    if (this.avaiableHubs.has(hubName)) {
      console.warn(`Hub with name ${hubName} is already registered. It will be overwritten.`)
    }
    this.avaiableHubs.set(hubName, hubInstance)
  }

  public getHub<T extends StorageService>(hubName: string): T | null {
    if (!this.avaiableHubs.has(hubName)) {
      console.warn(`Hub with name ${hubName} is not registered.`)
      return null
    }
    return this.avaiableHubs.get(hubName) as T
  }

  public subscribeToHub(hubName: string, subscription: AirSubscription) {
    // Subscribir a un hub usando el subscription bridge correspondiente, si el hub no está registrado aún, almacenar la subscripción en airSubscriptions para suscribirla cuando el hub se registre
    const hub = dataHubManager.checkStorage(hubName)
    if (hub) {
      if (subscription.pipeline) {
        dataHubManager.subscribePipeline(
          hubName,
          subscription.pipeline,
          subscription.subscriberID,
          subscription.callback,
        )
      } else {
        dataHubManager.subscribeStorage(hubName, subscription.subscriberID, subscription.callback)
      }
    } else {
      console.warn(`Hub with name ${hubName} is not registered yet. Storing subscription in airSubscriptions.`)
      this.setAirSubscription(hubName, subscription)
    }
  }

  private setAirSubscription(hubName: string, subscription: AirSubscription) {
    if (!this.airSubscriptions.has(hubName)) {
      this.airSubscriptions.set(hubName, [])
    }
    const subscriptions = this.airSubscriptions.get(hubName)!
    subscriptions.push(subscription)
  }
}

const dataHubServices = new DataHubServices()
export default dataHubServices
